import React, { useRef, useState } from 'react';
import { applyQuotaPreset, deleteQuotaPreset, errorLines, listQuotaPresets } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import type { QuotaPreset, TargetGroup } from '../../types';
import { Badge, Button, EmptyState, ErrorState, InlineAlert, Modal, Skeleton, cx, useToast } from '../../ui';
import { useAuth } from '../auth/AuthProvider';

interface QuotaPresetPickerProps {
  open: boolean;
  projectId: string;
  targetGroupId: string;
  onClose: () => void;
  /** the target group as returned after the preset was applied */
  onApplied: (group: TargetGroup) => void;
}

/**
 * "Apply preset": choose saved quota rows and copy them into this target group's quotas.
 * Presets shown are the company-wide ones plus the signed-in user's client's own (GET ?clientId=).
 * The server converts percent presets with this group's wanted completes, then checks the rows like a normal save:
 * on a problem it answers 400 with `errors` and changes nothing.
 */
export function QuotaPresetPicker({ open, projectId, targetGroupId, onClose, onApplied }: QuotaPresetPickerProps) {
  const { user } = useAuth();
  const { notify } = useToast();
  const clientId = user && typeof user.clientId === 'string' && user.clientId ? user.clientId : undefined;
  const presets = useAsyncData<QuotaPreset[]>((signal) => listQuotaPresets(clientId, { signal }), [clientId, open], { enabled: open });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) { setSelectedId(null); setConfirmDelete(null); setProblems([]); }
  }

  const selected = (presets.data || []).find((p) => p.id === selectedId) || null;

  const apply = async () => {
    if (!selected || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setProblems([]);
    try {
      const group = await applyQuotaPreset(projectId, targetGroupId, selected.id);
      notify(`Applied “${selected.name}”`, { tone: 'ok' });
      onApplied(group);
      onClose();
    } catch (err) {
      setProblems(errorLines(err));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await deleteQuotaPreset(id);
      notify('Preset deleted', { tone: 'ok' });
      setConfirmDelete(null);
      if (selectedId === id) setSelectedId(null);
      presets.reload();
    } catch (err) {
      setProblems(errorLines(err));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      size="lg"
      title="Apply a quota preset"
      subtitle="Copies the preset's rows into this target group's quotas."
      onClose={() => { if (!busyRef.current) onClose(); }}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={apply} loading={busy} disabled={!selected}>Apply preset</Button>
        </>
      )}
    >
      <InlineAlert tone="warn">This replaces the quotas of this target group, including changes on this page you haven't saved.</InlineAlert>

      <div className="mt-4">
        {presets.loading && !presets.data ? (
          <div className="space-y-2" aria-busy="true" aria-label="Loading presets">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : presets.error ? (
          <ErrorState title="Could not load presets" message={errorLines(presets.error).join(' ')} onRetry={presets.reload} />
        ) : (presets.data || []).length === 0 ? (
          <EmptyState title="No presets yet" description="Use “Save as preset” on a target group with saved quotas to create one." />
        ) : (
          <ul role="radiogroup" aria-label="Quota presets" className="space-y-2">
            {(presets.data || []).map((p) => {
              const on = p.id === selectedId;
              const rows = (p.data || []).length;
              return (
                <li key={p.id} className={cx('rounded-lg border px-3.5 py-3', on ? 'border-brand-300 bg-brand-50/50' : 'border-line')}>
                  <div className="flex items-start gap-3">
                    <input type="radio" name="quota-preset" aria-label={p.name} className="mt-1 h-4 w-4 accent-brand-600" checked={on} onChange={() => setSelectedId(p.id)} />
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
                        {p.name}
                        <Badge tone={p.clientId == null ? 'neutral' : 'brand'}>{p.clientId == null ? 'Company-wide' : p.clientId === clientId ? 'My client' : 'Client'}</Badge>
                      </p>
                      {p.description && <p className="mt-0.5 text-sm text-muted">{p.description}</p>}
                      <p className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] font-semibold text-muted">
                        <span className="rounded-full bg-chip px-2 py-0.5">{p.mode === 'nonInterlocking' ? 'Non-interlocking' : 'Interlocking'}</span>
                        <span className="rounded-full bg-chip px-2 py-0.5">{p.unit === 'percent' ? 'Percent of wanted completes' : 'Numbers'}</span>
                        <span className="rounded-full bg-chip px-2 py-0.5">{rows} {rows === 1 ? 'row' : 'rows'}</span>
                      </p>
                    </div>
                    {confirmDelete === p.id ? (
                      <span className="flex flex-none items-center gap-1.5 text-xs">
                        <span className="text-muted">Delete for everyone who can see it?</span>
                        <Button variant="danger" size="sm" onClick={() => remove(p.id)} disabled={busy}>Delete</Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(null)} disabled={busy}>Keep</Button>
                      </span>
                    ) : (
                      <Button variant="ghost" size="sm" aria-label={`Delete ${p.name}`} onClick={() => setConfirmDelete(p.id)} disabled={busy}>Delete</Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {selected && selected.unit === 'percent' && (
        <p className="mt-3 text-xs text-muted">Percent rows become whole numbers of this target group's wanted completes, adding up exactly. Save the basics first.</p>
      )}
      {problems.length > 0 && (
        <div className="mt-4">
          <InlineAlert>{problems.length === 1 ? problems[0] : <><b>{problems[0]}</b><ul className="ml-4 mt-1 list-disc">{problems.slice(1).map((m) => <li key={m}>{m}</li>)}</ul></>}</InlineAlert>
        </div>
      )}
    </Modal>
  );
}
